/* eslint-disable import/no-anonymous-default-export */

import { render } from "@testing-library/react";
import pica from 'pica';
import _ from 'lodash';

const canvas = document.createElement('canvas');
let lastImageIdDrawn;

/**
 * creates a cornerstone Image object for the specified Image and imageId
 *
 * @param image - An Image
 * @param imageId - the imageId for this image
 * @returns Cornerstone Image Object
 */
export default function (image, imageId) {
  // extract the attributes we need
  const rows = image.naturalHeight;
  const columns = image.naturalWidth;

  function getPixelData() {
    const imageData = getImageData();
    return imageData.data;
  }

  function getImageData() {
    let context;

    if (lastImageIdDrawn === imageId) {
      context = canvas.getContext('2d');
    } else {
      canvas.height = image.naturalHeight;
      canvas.width = image.naturalWidth;
      context = canvas.getContext('2d');
      context.drawImage(image, 0, 0);
      lastImageIdDrawn = imageId;
    }

    return context.getImageData(0, 0, image.naturalWidth, image.naturalHeight);
  }

  function getCanvas() {
    if (lastImageIdDrawn === imageId) {
      return canvas;
    }

    canvas.height = image.naturalHeight;
    canvas.width = image.naturalWidth;
    const context = canvas.getContext('2d');

    context.drawImage(image, 0, 0);
    lastImageIdDrawn = imageId;

    return canvas;
  }

  function getImage() {
    return image;
  }

  function gda(image, viewport = null) {
    if (viewport && viewport.displayedArea) {
      return viewport.displayedArea;
    }

    return {
      tlhc: {
        x: 1,
        y: 1
      },
      brhc: {
        x: image.columns,
        y: image.rows
      },
      rowPixelSpacing: image.rowPixelSpacing === undefined ? 1 : image.rowPixelSpacing,
      columnPixelSpacing: image.columnPixelSpacing === undefined ? 1 : image.columnPixelSpacing,
      presentationSizeMode: 'NONE'
    };
  }

  let lastRendered = null;
  let updateImage = null;
  let picaPromise = null;
  let picaCancelToken = null;
  let picaPendingJob = null;
  let lastScale = 0;

  const renderLocal = (enabledElement, invalidated) => {
    // enabledElement.viewport.pixelReplication = true;

    const image = enabledElement.image;
    const context = enabledElement.canvas.getContext('2d', {
      desynchronized: true
    });

    context.setTransform(1, 0, 0, 1, 0, 0);

    // Clear the canvas
    context.fillStyle = 'black';
    context.fillRect(0, 0, enabledElement.canvas.width, enabledElement.canvas.height);

    window.cornerstone.setToPixelCoordinateSystem(enabledElement, context);
    const imageDisplayedArea = gda(enabledElement.image, enabledElement.viewport);
    const sx = imageDisplayedArea.tlhc.x - 1;
    const sy = imageDisplayedArea.tlhc.y - 1;
    const width = imageDisplayedArea.brhc.x - sx;
    const height = imageDisplayedArea.brhc.y - sy;

    var offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = image.width;
    offscreenCanvas.height = image.height;
    offscreenCanvas.getContext('2d').drawImage(image.getImage(), sx, sy, width, height, sx, sy, width, height);

    var offscreenCanvas2 = document.createElement('canvas');

    const scale = enabledElement.viewport.scale;
    offscreenCanvas2.width = image.width * scale;
    offscreenCanvas2.height = image.height * scale;

    image._renderCnt++;

    let updateImageRequired = false;

    if(lastScale !== scale) {
      updateImageRequired = true;
      console.debug("updateImage: ", image.imageId, enabledElement.element.id, image._renderCnt);
    }

    lastScale = scale;

    if (!image._forceRender && lastRendered && image.getImage() === lastRendered.image) {
      console.debug("draw: ", image.imageId, enabledElement.element.id, image._renderCnt, lastRendered._renderCnt);
      context.drawImage(
        lastRendered.result,
        lastRendered.source.x,
        lastRendered.source.y,
        lastRendered.scaled.width,
        lastRendered.scaled.height,
        lastRendered.destination.x,
        lastRendered.destination.y,
        lastRendered.source.width,
        lastRendered.source.height
      );
    } else {
      updateImageRequired = true;
      console.debug("updateImage + draw: ", image.imageId, enabledElement.element.id, image._renderCnt, image._forceRender);
      context.drawImage(image.getImage(), sx, sy, width, height, sx, sy, width, height);
    }

    // need to update an image
    if(updateImageRequired && !image._forceRender) {
      if (updateImage != null) {
        updateImage.cancel();
        updateImage = null;
      }

      updateImage = _.debounce(async () => {
        // image version for the moment of entering updateImage
        const dbRenderCnt = image._renderCnt;

        // cancel pica job if required
        if (picaCancelToken) {
          console.debug('updateImage.picaCancelToken:', image.imageId, enabledElement.element.id, image._renderCnt, dbRenderCnt);
          picaCancelToken('updateImage.picaCancel');
          picaCancelToken = null;
        }

        // abort prev updateImage
        if (picaPendingJob) {
          console.debug('updateImage.draw2: ABORTING', image.imageId, enabledElement.element.id, image._renderCnt, dbRenderCnt);
          picaPendingJob.abort();
        }

        picaPendingJob = new AbortController();
        let localSignal = picaPendingJob.signal;

        // wait for prev pica job to finish - the idea is to run only a single 'update/resize' per image
        if (picaPromise) {
          try {
            console.debug("updateImage.draw2: AWAIT", image.imageId, enabledElement.element.id, image._renderCnt, dbRenderCnt);
            await picaPromise;
          } catch (e) {
            console.error('updateImage.picaPromise', e);
          }
          picaPromise = null;
        }

        // exit when the next updateImage abort this instance of updateImage
        if (localSignal.aborted) {
          console.debug('updateImage.draw2: ABORTED', image.imageId, enabledElement.element.id, image._renderCnt, dbRenderCnt);
          return;
        }

        // source document has been changed - do nothing
        if (!image.imageId.startsWith(enabledElement.element.id)) {
          console.debug("updateImage.draw2: CANCELED", image.imageId, enabledElement.element.id, image._renderCnt, dbRenderCnt);
          return;
        }

        // create pica job
        console.debug("updateImage.draw2: PICA", image.imageId, enabledElement.element.id, image._renderCnt, dbRenderCnt);
        picaPromise = new Promise((resolve, reject) => {
          pica().resize(offscreenCanvas, offscreenCanvas2, {
            quality: 3,
            unsharpAmount: 80,
            unsharpRadius: 0.6,
            unsharpThreshold: 2,
            cancelToken: new Promise((resolve, reject) => {
              picaCancelToken = reject;
            }),
          }).then(result => {
              lastRendered = {
                image: image.getImage(),
                result: result,             // The image or result object
                source: {
                  x: sx,                    // Source X coordinate
                  y: sy,                    // Source Y coordinate
                  width: width,             // Original width
                  height: height            // Original height
                },
                scaled: {
                  width: width * scale,     // Scaled width
                  height: height * scale    // Scaled height
                },
                destination: {
                  x: sx,                    // Destination X coordinate (could be different)
                  y: sy                     // Destination Y coordinate (could be different)
                },
                _renderCnt: dbRenderCnt     // version of source image used for this 'update/resize'
              };

              picaCancelToken = null;
              picaPromise = null;

              if (!image.imageId.startsWith(enabledElement.element.id)) {
                console.debug("updateImage.draw: CANCELED", image.imageId, enabledElement.element.id, image._renderCnt, dbRenderCnt);
                return;
              }

              console.debug("updateImage.draw: ", image.imageId, enabledElement.element.id, image._renderCnt, dbRenderCnt);
              context.drawImage(result, sx, sy, width * scale, height * scale, sx, sy, width, height);

              const cornerstone = window.cornerstone;
              const eventData = {
                viewport: enabledElement.viewport,
                element: enabledElement.element,
                image: enabledElement.image,
                enabledElement,
                canvasContext: enabledElement.canvas.getContext('2d', {
                  desynchronized: true
                }),
                renderTimeInMs: 0
              };

              cornerstone.triggerEvent(enabledElement.element, 'cornerstoneimagerendered', eventData);

              resolve();
            })
            .catch(err => {
                  picaCancelToken = null;
                  picaPromise = null;
                  if (err !== 'updateImage.picaCancel') {
                    console.error(err);
                  }
                  reject(err);
                }
            );
        });
      }, 10);

      updateImage();
    }

    // window.cornerstone.renderWebImage(enabledElement, invalidated);
  }

  // Extract the various attributes we need
  return {
    imageId,
    minPixelValue: 0,
    maxPixelValue: 255,
    slope: 1,
    intercept: 0,
    windowCenter: 128,
    windowWidth: 255,
    // render: window.cornerstone.renderWebImage,
    render: renderLocal,
    getPixelData,
    getCanvas,
    getImage,
    rows,
    columns,
    height: rows,
    width: columns,
    color: true,
    rgba: false,
    columnPixelSpacing: undefined,
    rowPixelSpacing: undefined,
    invert: false,
    sizeInBytes: rows * columns * 4,
    _renderCnt: 0,
    _forceRender: false
  };
}
